/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Vue, Component } from 'vue-property-decorator';
import {
  Toast, Loading, Dialog, Form, DialogEventNames,
} from '@hb/coffee';
import ButtonBar from '@/components/button-bar';
import { SupportComponents } from '@/components/supplement-info-fields/components';
import { getPersonalAdditionalInfo, updateUserAdditionalInfo } from '@/api/userPersonalInfo';
import {
  checkTelPhone,
  checkEmail,
  checkCompanyDetails,
  checkAddressSensitive,
  checkDetailAddressLength,
  validarorTips,
  checkCompanyName,
} from '@/shared/utils/validator';
import type { AddressForm } from '@/shared/utils/areaPicker/types';
import type { UserAdditionalInfoType } from '@/api/userPersonalInfo/types';
import { leadTrailTrim, trimAll } from '@/shared/utils/trim';


const style = require('./index.module.scss') as Dictionary;

// 输入框
const fieldsList = [
  {
    name: '个人资料',
    fields: [
      'maritalStatus', 'liveStatus', 'commonEmail',
    ],
  },
  {
    name: '工作及地址信息',
    fields: [
      'profession', 'position', 'role', 'industry', 'companyName', 'companyNature', 'companyAddress', 'companyAddressDetails', 'companyPhone', 'debtSituation', 'sourceOfIncome',
    ],
  },
];

interface validatorListType {
  [key: string]: (val: string, set: (msg: string) => void) => boolean
}

interface refElement extends Vue {
  onValidator: () => boolean;
}

const response = {
  success: 1,
};

const keyboardAppear = 'keyboard-appear';
const keyboardDisappear = 'keyboard-disappear';

interface rulesType {
  errorMsg: string, rule: (val: string) => boolean
}

@Component({ name: 'BaseForm' })
export default class BaseForm extends Vue {
  // 表单值
  private modelValue: UserAdditionalInfoType = {
    maritalStatus: undefined, // 婚姻状况
    liveStatus: undefined, // 居住状态
    commonEmail: undefined, // 电子邮件
    profession: undefined, // 职业
    position: undefined, // 职务
    role: undefined, // 工作性质
    industry: undefined, // 所属行业
    companyName: undefined, // 公司名称
    companyNature: undefined, // 单位性质
    companyAddress: undefined, // 单位地址
    companyAddressDetails: undefined, // 详细地址
    companyPhone: undefined,
    debtSituation: undefined, // 已有负债
    sourceOfIncome: undefined, // 收入来源
    id: undefined,
  };

  // 初始页面时会赋值
  private storeInitValue: UserAdditionalInfoType = {} as UserAdditionalInfoType;

  // 是否获取到初始值
  private isReady = false;

  private isShowButton = true;

  private keyboardTimer: number | null = null;

  private nameMap: Record<string, {name: string, rules:rulesType[]}> = {
    commonEmail: {
      name: '邮箱',
      rules: [
        {
          rule: (val: string) => checkEmail(val),
          errorMsg: '请输入正确的邮箱',
        },
      ],
    },
    companyName: {
      name: '公司名称',
      rules: [
        {
          rule: (val: string) => checkAddressSensitive(val),
          errorMsg: validarorTips.addressSensitiveTip,
        },
        {
          rule: (val: string) => checkCompanyName(val),
          errorMsg: '请填写正确的公司名称(不少于5个汉字)',
        },
      ],
    },
    companyAddressDetails: {
      name: '详细地址',
      rules: [
        {
          rule: (val: string) => checkAddressSensitive(val),
          errorMsg: validarorTips.addressSensitiveTip,
        },
        {
          rule: (val: string) => checkDetailAddressLength(val),
          errorMsg: validarorTips.addressLengthTip,
        },
        {
          rule: (val: string) => checkCompanyDetails(val),
          errorMsg: validarorTips.addressContentTip,
        },
      ],
    },
    companyPhone: {
      name: '公司电话',
      rules: [
        {
          rule: (val: string) => checkTelPhone(val),
          errorMsg: '请输入正确的公司电话',
        },
      ],
    },
  }

  private validatorList: validatorListType = {};

  // 地址是否有效
  private isAddressValid(address?: AddressForm): boolean {
    return !!(address && address.province && address.city && address.district);
  }

  private async created() {
    this.processKeyboard();
    const res = await getPersonalAdditionalInfo<UserAdditionalInfoType>();
    Object.keys(this.modelValue).forEach((key: keyof UserAdditionalInfoType) => {
      this.storeInitValue[key] = res[key];
      this.modelValue[key] = res[key];
    });

    // validator 逻辑
    const validators: validatorListType = {};
    ['commonEmail', 'companyName', 'companyAddress', 'companyAddressDetails', 'companyPhone'].forEach((key) => {
      validators[key] = (val: string | AddressForm, set: (msg: string) => void): boolean => {
        // 若单位详细地址已填 但 单位地址未填，拦截
        if (key === 'companyAddress' && !this.isAddressValid(val as AddressForm) && this.modelValue.companyAddressDetails) {
          set('请输入单位地址再进行保存');
          return false;
        }
        // 若单位地址已填 但 单位详细地址未填，拦截
        if (key === 'companyAddressDetails' && !val && this.isAddressValid(this.modelValue.companyAddress)) {
          set('请输入详细地址再进行保存');
          return false;
        }

        if (!val) {
          if (this.storeInitValue[key]) { // 曾经填写的已有资料不能清空
            set(`已填写过${this.nameMap[key].name}，不能清空`);
            return false;
          }
          return true;
        }
        if (key !== 'companyAddress') {
          return !this.nameMap[key].rules.some((ruleItem) => {
            if (!ruleItem.rule(val as string)) {
              set(ruleItem.errorMsg);
              return true;
            }
            return false;
          });
        }
        return true;
      };
    });
    this.validatorList = validators;
    this.isReady = true;
  }

  private beforeDestroy() {
    window.removeEventListener(keyboardAppear, this.onKeyboardAppear);
    window.removeEventListener(keyboardDisappear, this.onKeyboardDisappear);

    this.clearKeyboardTimer();
  }

  // VirtualKeyboard API兼容太差了 还是用已有的代码
  private processKeyboard() {
    window.addEventListener(keyboardAppear, this.onKeyboardAppear);
    window.addEventListener(keyboardDisappear, this.onKeyboardDisappear);
  }

  private clearKeyboardTimer() {
    if (this.keyboardTimer) {
      window.clearTimeout(this.keyboardTimer);
      this.keyboardTimer = null;
    }
  }

  // 键盘打开
  private onKeyboardAppear() {
    this.clearKeyboardTimer();

    this.isShowButton = false;
  }

  // 键盘关闭
  private onKeyboardDisappear() {
    // 防止重新展示时，按钮定位偏移
    // 软键盘动画标准时长250ms
    // 150ms值，在键盘动画还未结束前，展示按钮，可以规避按钮展示抖动
    this.keyboardTimer = window.setTimeout(() => {
      this.isShowButton = true;
    }, 150);
  }

  private async handleConfirm() {
    this.$ubtPageButton('finance_loan_addInformation_save');
    const res = await updateUserAdditionalInfo({
      ...this.modelValue,
      // 地址信息，去除首尾的空格
      companyAddressDetails: leadTrailTrim(this.modelValue.companyAddressDetails || ''),
      companyPhone: this.modelValue.companyPhone && trimAll(this.modelValue.companyPhone),
    });
    if (res === response.success) {
      Toast.success('提交成功');
      this.$router.go(-1);
    }
  }

  private handleChange(key: keyof UserAdditionalInfoType, value: string | number | undefined) {
    this.modelValue[key] = value;
  }

  private itemValidator(key:string): boolean {
    const itemElement = this.$refs[key] as refElement;
    return itemElement.onValidator();
  }

  private validator(): boolean {
    // 只用校验手动输入的字段 其他选择框的字段可为空(非必填字段)
    const email = this.$refs.commonEmail as refElement;
    const companyName = this.$refs.companyName as refElement;
    const companyAddress = this.$refs.companyAddress as refElement;
    const companyAddressDetails = this.$refs.companyAddressDetails as refElement;
    const companyPhone = this.$refs.companyPhone as refElement;
    return (email.onValidator() && companyName.onValidator() && companyAddress.onValidator()
    && companyAddressDetails.onValidator() && companyPhone.onValidator());
  }

  private beforeSave() {
    this.$ubtModuleExpose('finance_loan_addInformation_dialog');

    if (this.validator()) {
      Dialog.confirm({
        title: '是否保存修改',
        confirmButtonText: '保存',
        cancelButtonText: '放弃',
        onEvent: (emitName: DialogEventNames) => {
          if (emitName === 'closeIconClick') {
            this.$ubtPageButton('finance_loan_addInformation_cancel');
          }
        },
      }).then(() => {
        this.handleConfirm();
      }).catch(() => {
        this.$ubtPageButton('finance_loan_addInformation_cancel');
      });
    }
  }

  private render() {
    return (
      <div class={style.main}>
        {
          this.isReady ? (
            fieldsList.map((block) => (
              [
                <div class={style.title}>{block.name}</div>,
                <Form divider round>
                  {
                    ...block.fields.map((key, i) => {
                      const ComponentElement = SupportComponents.getComponent(key);
                      return ComponentElement ? (
                          <ComponentElement
                            ref={key}
                            validator={this.validatorList[key]}
                            value={this.modelValue[key]}
                            onBlur={this.itemValidator.bind(this, key)}
                            onChange={(propValue: string) => this.handleChange(key, propValue)}
                          />
                      ) : null;
                    })
                }
                </Form>,
              ]
            ))
          ) : (
            <div class={style.loading}>
              <Loading size={30} />
            </div>
          )
        }
        <div class={style.buttonContainer} v-show={this.isShowButton}>
            <ButtonBar
              disabled={false}
              confirmText={'保存修改'}
              onConfirm={this.beforeSave.bind(this)}
            />
        </div>
      </div>
    );
  }
}
